import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";

export interface VulnerabilityOption {
  id: number;
  value: string;
  score: number;
}

interface VulnerabilitySelectProps<T extends FieldValues> {
  name: FieldPath<T>;
  label: string;
  options: VulnerabilityOption[];
  control: Control<T>;
  formStyles: object;
  scoreLabel: string;
  canEdit: boolean;
}

export function VulnerabilitySelect<T extends FieldValues>({
  name,
  label,
  options,
  control,
  formStyles,
  scoreLabel,
  canEdit,
}: Readonly<VulnerabilitySelectProps<T>>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const selectedOption =
          options.find((o) => o.id === field.value) || null;

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "text.secondary" }}
            >
              {label}
            </Typography>

            <Autocomplete
              options={options}
              value={selectedOption}
              getOptionLabel={(option) => option.value}
              isOptionEqualToValue={(option, value) =>
                option.id === value.id
              }
              onChange={(_, newValue) =>
                field.onChange(newValue?.id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={formStyles}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
              disabled={!canEdit}
            />

            {selectedOption && !fieldState.error && (
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.secondary" }}
              >
                {scoreLabel}: {selectedOption.score}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
}
